/**
 * "File" gadget (Build): one project file the person picked, on Home. A Markdown file shows rendered as GitHub shows it
 * (markdown/MarkdownView.jsx: GFM, Mermaid diagrams, heading anchors, highlighted code, the project's own images and
 * links; no raw HTML, so nothing in the file runs on Flow's page); an image (png, jpg, gif, svg, webp) shows as an
 * image. The choice is kept with the layout (HomeGadgets `settings`), one file per project. Nothing picked: the
 * project's README, as on GitHub (the root's first, else the least deep one), or a hint when there is none. Loading, a
 * file that is no longer there and a read that failed each say so; "Open" opens the file (`onOpenFile`, DashboardView:
 * workspaceModel openProjectFile, a service's file in Code on that service, any other in the Files tab), "Change" picks
 * another.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as vfsClient from '../../../utils/vfsClient';
import { mimeTypeFor } from '../../../utils/binaryFiles';
import { findReadme } from '../../../utils/markdownLinks';
import MarkdownView from '../../markdown/MarkdownView';
import GadgetCard, { Quiet } from './GadgetCard';

const MARKDOWN = /\.(md|markdown)$/i;
const IMAGE = /\.(png|jpe?g|gif|svg|webp)$/i;
// At most this many matches in the picker (type to narrow).
const PICK_LIMIT = 200;

/** How the gadget shows `path`: 'markdown' | 'image' | null (it cannot). */
export const fileGadgetKind = (path) => (MARKDOWN.test(path || '') ? 'markdown' : IMAGE.test(path || '') ? 'image' : null);

/** How the gadget shows a README: Markdown, or as plain text (`README`, `README.txt`, as GitHub does). */
const readmeKind = (path) => fileGadgetKind(path) || 'text';

/**
 * The project's README (project-root-relative path) or null: the root's when there is one, else the least deep one
 * (a service's own). The root is listed alone first, so a project with a root README never lists its whole tree.
 */
export async function lookUpReadme(projectId) {
  const root = findReadme(await vfsClient.listFilePaths(projectId, { depth: 1 }));
  if (root) { return root; }
  return findReadme(await vfsClient.listFilePaths(projectId, { depth: 3 }));
}

/** The chosen file of `projectId` in the gadget's settings (`{ byProject: { [projectId]: path } }`), or null. */
export const chosenPath = (settings, projectId) => settings?.byProject?.[projectId] || null;

const baseName = (path) => String(path || '').split('/').pop();

/** The picker: the project's Markdown files and images, narrowed as one types; Escape or Cancel closes it. */
function FilePicker({ projectId, current = null, onPick, onCancel }) {
  const [state, setState] = useState({ status: 'loading', paths: [] });
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    vfsClient.listFilePaths(projectId)
      .then((paths) => { if (alive) { setState({ status: 'ready', paths: paths.filter((path) => fileGadgetKind(path)).sort((a, b) => a.localeCompare(b)) }); } })
      .catch((failure) => { if (alive) { setState({ status: 'error', paths: [], error: failure?.message || 'The files could not be listed' }); } });
    return () => { alive = false; };
  }, [projectId]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return state.paths.filter((path) => words.every((word) => path.toLowerCase().includes(word))).slice(0, PICK_LIMIT);
  }, [state.paths, query]);

  return (
    <div className="gd-file-picker" data-testid="file-gadget-picker" onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); onCancel(); } }} role="group" aria-label="Choose a file">
      <div className="gd-file-picker-head">
        <input
          aria-label="Find a Markdown file or an image"
          className="gd-file-search"
          data-testid="file-gadget-search"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a .md file or an image"
          ref={inputRef}
          type="search"
          value={query}
        />
        <button className="ph-link" data-testid="file-gadget-cancel" onClick={onCancel} type="button">Cancel</button>
      </div>
      {state.status === 'loading' && <Quiet testId="file-gadget-picker-loading">Listing the project&apos;s files…</Quiet>}
      {state.status === 'error' && <p className="gd-error" role="alert">{state.error}</p>}
      {state.status === 'ready' && matches.length === 0 && (
        <Quiet testId="file-gadget-picker-empty">{state.paths.length === 0 ? 'The project has no Markdown file or image yet.' : 'No file matches.'}</Quiet>
      )}
      {matches.length > 0 && (
        <ul className="gd-file-list" aria-label="Files">
          {matches.map((path) => (
            <li key={path}>
              <button aria-current={path === current ? 'true' : undefined} className="gd-file-option" data-testid={`file-gadget-option-${path}`} onClick={() => onPick(path)} type="button">
                <strong>{baseName(path)}</strong>
                <span>{path}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

FilePicker.propTypes = {
  current: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default function FileGadget({ projectId, settings = {}, onSettings, onOpenFile = null }) {
  const picked = chosenPath(settings, projectId);
  const [picking, setPicking] = useState(false);
  const [content, setContent] = useState({ status: 'idle' });
  // Nothing picked: the README is looked up ('looking'), then shown ('found') or there is none ('none').
  const [readme, setReadme] = useState({ status: 'idle', path: null });

  useEffect(() => {
    if (picked) { setReadme({ status: 'idle', path: null }); return undefined; }
    let alive = true;
    setReadme({ status: 'looking', path: null });
    lookUpReadme(projectId)
      .then((found) => { if (alive) { setReadme(found ? { status: 'found', path: found } : { status: 'none', path: null }); } })
      .catch(() => { if (alive) { setReadme({ status: 'none', path: null }); } });
    return () => { alive = false; };
  }, [projectId, picked]);

  const path = picked || readme.path;
  const kind = picked ? fileGadgetKind(picked) : (readme.path ? readmeKind(readme.path) : null);

  useEffect(() => {
    if (!path || !kind) { setContent({ status: 'idle' }); return undefined; }
    let alive = true;
    let url = null;
    setContent({ status: 'loading' });
    const read = kind === 'image'
      ? vfsClient.readBinaryFile(projectId, path, mimeTypeFor(baseName(path))).then((blob) => {
        if (!blob) { return { missing: true }; }
        url = URL.createObjectURL(blob);
        return { value: url };
      })
      : vfsClient.readFile(projectId, path).then((value) => ({ value }), (failure) => {
        if (/\(404\)/.test(failure?.message || '')) { return { missing: true }; }
        throw failure;
      });
    read.then((result) => {
      if (!alive) { return; }
      setContent(result.missing ? { status: 'missing' } : { status: 'ready', value: result.value });
    }).catch((failure) => { if (alive) { setContent({ status: 'error', error: failure?.message || 'The file could not be read' }); } });
    return () => {
      alive = false;
      if (url) { URL.revokeObjectURL(url); }
    };
  }, [projectId, path, kind]);

  const pick = (next) => {
    onSettings({ ...settings, byProject: { ...(settings.byProject || {}), [projectId]: next } });
    setPicking(false);
  };
  const changeLink = <button className="ph-link" data-testid="file-gadget-change" onClick={() => setPicking(true)} type="button">{picked ? 'Change' : 'Choose a file'}</button>;
  const links = (
    <>
      {path && content.status !== 'missing' && onOpenFile && (
        <button className="ph-link" data-testid="file-gadget-open" onClick={() => onOpenFile(path)} title={`Open ${path}`} type="button">Open</button>
      )}
      {!picking && changeLink}
    </>
  );

  let body;
  if (picking) {
    body = <FilePicker current={path} onCancel={() => setPicking(false)} onPick={pick} projectId={projectId} />;
  } else if (!path && readme.status !== 'none') {
    body = <Quiet testId="file-gadget-loading">Looking for the project&apos;s README…</Quiet>;
  } else if (!path) {
    body = <Quiet testId="file-gadget-empty">This project has no README yet. Pick a Markdown file or an image of the project to show it here.</Quiet>;
  } else if (content.status === 'loading' || content.status === 'idle') {
    body = <Quiet testId="file-gadget-loading">Loading {baseName(path)}…</Quiet>;
  } else if (content.status === 'missing') {
    body = <Quiet testId="file-gadget-missing">{path} is not in the project any more. Choose another file.</Quiet>;
  } else if (content.status === 'error') {
    body = <p className="gd-error" data-testid="file-gadget-error" role="alert">{baseName(path)} could not be read: {content.error}</p>;
  } else if (kind === 'image') {
    body = <div className="gd-file-image"><img alt={baseName(path)} data-testid="file-gadget-image" src={content.value} /></div>;
  } else if (kind === 'text') {
    body = <pre className="gd-file-md gd-file-text" data-testid="file-gadget-text">{content.value}</pre>;
  } else {
    body = <MarkdownView className="gd-file-md" filePath={path} onOpenFile={onOpenFile} projectId={projectId} source={content.value} testId="file-gadget-md" />;
  }

  return (
    <GadgetCard links={links} meta={path && !picking ? <span className="gd-file-name" title={picked ? path : `${path}: the project's README, shown until you choose a file`}>{baseName(path)}</span> : null} testId="gadget-file-card" title="File">
      {body}
    </GadgetCard>
  );
}

FileGadget.propTypes = {
  // (path) => void: opens the file (a file's path, never a folder to root a tree at); none: no Open link.
  onOpenFile: PropTypes.func,
  // Saves the gadget's settings with the layout.
  onSettings: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  settings: PropTypes.shape({ byProject: PropTypes.objectOf(PropTypes.string) }),
};
